import React from "react";
import Seo from "../components/Seo";
import { Section, SectionLabel, Band } from "../components/Section";
import { Table } from "../components/Table";
import EarlyAccessForm from "../components/EarlyAccessForm";
import { Layers, FileLock2, Database, LayoutDashboard, LockKeyhole, ShieldCheck, Landmark, BriefcaseBusiness, FlaskConical, Stethoscope, Store } from "lucide-react";
import { EditorialIcon, ArchitectureIllustration } from "../components/EditorialVisuals";
import { ContainmentDiagram } from "../components/EditorialDiagrams";

const LEAD = "Technology that confines AI in a box you control. AI cannot do, cannot touch, cannot see what you don't want it to.";

const CANNOT = ["Cannot do what you have not permitted.", "Cannot touch systems outside its mandate.", "Cannot see data you have not opened to it."];

const STATS = [
  { num: "30+", label: "Governance, orchestration and execution engines" },
  { num: "1,000+", label: "Gates for a personal workspace" },
  { num: "5,000+", label: "Gates for an enterprise of 3,000+ people" },
];

const FEATURES = [
  {
    title: "One environment, many models.",
    body: "Your teams work in one place with the freedom to use multiple AI models and approved AI tools, with your data protected and AI performance intact.",
  },
  { title: "Protection in prompts and files.", body: "Rules you set flag, mask or block confidential details before they reach an AI model." },
  {
    title: "Control over your data.",
    body: "You decide where it is stored, how long it is retained, and what AI can access and do with it, synced to your workflow.",
  },
  {
    title: "One oversight view.",
    body: "AI activity, access permissions and alerts in one organised, customisable dashboard, so risky sharing is visible and can be acted on.",
  },
  { title: "Gates enforced at rest and on the go.", body: "Your requirements apply wherever the data sits and wherever the AI acts." },
  { title: "Embedded insurance.", body: "Holistic assurance and enforcement, built into the box." },
];

const SCENES = [
  { role: "Bank manager", quote: "Go ahead and use AI to compare the applications. Aky will apply our customer data protections." },
  { role: "Procurement lead", quote: "Let AI help you review the bids. Aky will keep its use within our approved rules." },
  { role: "Product development head", quote: "You can use AI for the product review. Aky will restrict access to the formula." },
  { role: "Senior clinician", quote: "Use AI to help prepare the referral. Aky will mask out our patient personal information." },
  { role: "Business owner", quote: "Tumia AI for the submission. With Aky, I can monitor how our company information is being used." },
];

const LAUNCH = [
  ["October 2026", "Aky unveiled at the launch briefing in Nairobi"],
  ["November 2026", "Early access opens to the first 100 companies"],
  ["January 2027", "General availability"],
];

const FEATURE_ICONS = [Layers, FileLock2, Database, LayoutDashboard, LockKeyhole, ShieldCheck];
const SCENE_ICONS = [Landmark, BriefcaseBusiness, FlaskConical, Stethoscope, Store];

export default function Aky() {
  return (
    <>
      <Seo title="Aky · AI in a Box — Syni.ai" description={LEAD} path="/aky" />

      <section className="hero hero--page" data-testid="aky-hero">
        <div className="container-syni">
          <h1 className="h-page hero-headline" data-testid="aky-hero-title">
            Aky <span className="descriptor">· AI in a Box</span>
          </h1>
          <p className="lead hero-sub" data-testid="aky-hero-lead">
            {LEAD}
          </p>
          <ContainmentDiagram items={CANNOT} testId="aky-containment-diagram" />
        </div>
      </section>

      <Section n="01" label="Why a box" split pale testId="aky-why" visual={<ArchitectureIllustration kind="insulation" testId="aky-insulation-illustration" />} below={
        <div className="stats" data-testid="aky-stats">
          {STATS.map((s) => (
            <div key={s.num} className="stat">
              <div className="stat-num">{s.num}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      }>
        <p className="body-copy measure">
          To keep AI from seeing what it shouldn't, from hallucinating, fabricating or decaying, and to keep it performing without blowing the
          budget, you need an environment built for it. Aky is that environment: your rules, applied once, enforced wherever the data sits
          and wherever the AI acts.
        </p>
      </Section>

      <Section n="02" label="What Aky does" title="You plug it in once. From then on:" testId="aky-does">
        <div className="features" data-testid="aky-features">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="feature feature--illustrated" data-testid={`aky-feature-${i + 1}`}>
              <EditorialIcon icon={FEATURE_ICONS[i]} testId={`aky-feature-icon-${i + 1}`} />
              <div>
                <div className="feature-title" data-testid={`aky-feature-title-${i + 1}`}>{f.title}</div>
                <div className="feature-body" data-testid={`aky-feature-body-${i + 1}`}>{f.body}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section n="03" label="What it looks like in daily work" pale testId="aky-scenes">
        <div className="grid-cards grid-cards--2" data-testid="aky-scene-cards">
          {SCENES.map((s, i) => (
            <div key={s.role} className={`scene${i === SCENES.length - 1 ? " scene--wide" : ""}`} data-testid={`aky-scene-${i + 1}`}>
              <div className="icon-heading">
                <EditorialIcon icon={SCENE_ICONS[i]} testId={`aky-scene-icon-${i + 1}`} />
                <span className="mono-label" data-testid={`aky-scene-role-${i + 1}`}>{s.role}</span>
              </div>
              <p className="scene-quote" data-testid={`aky-scene-quote-${i + 1}`}>“{s.quote}”</p>
            </div>
          ))}
        </div>
        <p className="scenes-close" data-testid="aky-scenes-close">
          Power your team with AI. Your rules. Your data under your control.
        </p>
      </Section>

      <Section n="04" label="Launch path" testId="aky-launch">
        <Table columns={["When", "What"]} rows={LAUNCH} variant="kv" testId="aky-launch-table" />
      </Section>

      <Band
        label="Apply for early access"
        title="Early access is for enterprises that want AI working inside their own box before regulation arrives."
        body="Places are limited to 100 and allocated on application. Early-access companies run Aky in their own environment, on their own use cases, with our team alongside them, and shape the product before general release."
        testId="aky-band"
      />

      <section className="section" id="apply" data-testid="aky-form-section">
        <div className="container-syni">
          <div className="section-head">
            <SectionLabel>Form</SectionLabel>
            <h2 className="h-section">Apply for early access</h2>
          </div>
          <div className="form-wrap">
            <EarlyAccessForm />
          </div>
        </div>
      </section>
    </>
  );
}
